import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Logo from "../components/Logo";
import { ArrowRight } from "lucide-react";

export default function Register() {
  const { register, user, subdomainCompany } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", company_slug: "", company_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/companies").then((r) => {
      setCompanies(r.data);
      // On a company subdomain, lock the signup to that company; otherwise default to first
      const preferred = subdomainCompany ? r.data.find((c) => c.slug === subdomainCompany.slug) : r.data[0];
      if (preferred) setForm((f) => ({ ...f, company_slug: preferred.slug, company: preferred.name }));
    }).catch(() => {});
  }, [subdomainCompany]);

  if (user) return <Navigate to="/dashboard" replace />;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="w-full max-w-md mx-auto animate-fade-up">
          <Logo className="mb-12" brand={subdomainCompany} />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Nouveau compte</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Créer un compte</h1>
          <p className="text-muted-foreground mb-8">Rejoignez l'équipe commerciale {subdomainCompany?.name || "OXIWI"}.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label>Société</Label>
              <Select value={form.company_slug} disabled={!!subdomainCompany} onValueChange={(v) => setForm({ ...form, company_slug: v, company: companies.find((c) => c.slug === v)?.name || "" })}>
                <SelectTrigger className="mt-1.5 rounded-sm h-11" data-testid="register-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((b) => (
                    <SelectItem key={b.slug} value={b.slug}>{b.name}{b.industry ? ` — ${b.industry}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="name">Nom complet</Label>
              <Input id="name" required value={form.name} data-testid="register-name-input"
                onChange={upd("name")} placeholder="Jean Dupont" className="mt-1.5 rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} data-testid="register-email-input"
                onChange={upd("email")} placeholder="vous@oxiwi.com" className="mt-1.5 rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required value={form.password} data-testid="register-password-input"
                onChange={upd("password")} placeholder="••••••••" className="mt-1.5 rounded-sm h-11" />
            </div>
            <div className="pt-2 border-t border-border">
              <Label htmlFor="company_name">Nom de votre société <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Un espace de travail sera créé automatiquement pour votre équipe.</p>
              <Input id="company_name" value={form.company_name} data-testid="register-company-name-input"
                onChange={upd("company_name")} placeholder="Acme Agency" className="rounded-sm h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit-button"
              className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-base font-semibold group">
              {loading ? "Création…" : "Créer mon compte"}
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login">Se connecter</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-foreground">
        <img src="https://images.pexels.com/photos/9300768/pexels-photo-9300768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="OXIWI" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/40 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-3 max-w-md leading-tight">L'outil des commerciaux télécom performants.</h2>
          <p className="text-white/70 max-w-md">Modèles prêts à l'emploi, génération IA et suivi en temps réel.</p>
        </div>
      </div>
    </div>
  );
}
